import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Users, Copy, Check, Gift, Share2, UserPlus, Clock, CheckCircle2, Sparkles, MousePointerClick
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

function useReferralCode() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['referral-code', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referral_codes')
        .select('*')
        .eq('owner_user_id', user!.id)
        .eq('active', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

function useReferrals() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['referrals', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('referrals')
        .select('*')
        .eq('referrer_user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!user?.id,
  });
}

function useReferralConfig() {
  return useQuery({
    queryKey: ['referral-config'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('referral_config');
      if (error) throw error;
      return (data ?? {}) as {
        referrer_discount_percent?: number;
        referred_discount_percent?: number;
        reward_valid_days?: number;
      };
    },
  });
}

function useMyReferralStats() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-referral-stats', user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('my_referral_stats');
      if (error) throw error;
      return (data ?? {}) as {
        clicks?: number; total?: number; registered?: number; converted?: number;
        pendientes_compra?: number; con_compra?: number; reversados?: number;
        beneficios_generados?: number; descuento_disponible?: number; descuentos_usados?: number;
      };
    },
    enabled: !!user?.id,
  });
}

function useMyRewards() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['referral-rewards', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('discount_codes')
        .select('id, code, discount_value, expires_at, active, times_used, max_uses')
        .eq('specific_user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) {
        console.error('rewards query error', error);
        throw error;
      }
      return data ?? [];
    },
    enabled: !!user?.id,
  });
}


const ReferralsSection = () => {
  const [copied, setCopied] = useState(false);
  const { data: referralCode } = useReferralCode();
  const { data: referrals = [] } = useReferrals();
  const { data: config } = useReferralConfig();
  const { data: stats } = useMyReferralStats();
  const { data: rewards = [], isLoading: rewardsLoading, error: rewardsError } = useMyRewards();

  const code = referralCode?.code ?? "—";
  const referralLink = referralCode ? `${window.location.origin}/auth?mode=signup&ref=${code}` : "";

  const totalReferrals = referrals.length;
  const registered = referrals.filter(r => r.status === 'registered' || r.status === 'converted').length;
  const converted = referrals.filter(r => r.status === 'converted').length;
  const clicks = Number(stats?.clicks ?? 0);
  const clickToSignup = clicks > 0 ? Math.round((registered / clicks) * 100) : 0;
  const referrerPercent = config?.referrer_discount_percent ?? 0;
  const referredPercent = config?.referred_discount_percent ?? 0;

  // Resumen por estados (spec). Usa los stats del server con fallback client-side.
  const pendientesCompra = Number(stats?.pendientes_compra ?? referrals.filter((r: any) => r.status === 'registered').length);
  const conCompra = Number(stats?.con_compra ?? converted);
  const beneficiosGenerados = Number(stats?.beneficios_generados ?? converted);
  const descuentoDisponible = Number(stats?.descuento_disponible ?? rewards.filter((r: any) => r.active && (r.times_used ?? 0) === 0 && (!r.expires_at || new Date(r.expires_at) > new Date())).length);
  const descuentosUsados = Number(stats?.descuentos_usados ?? rewards.filter((r: any) => (r.times_used ?? 0) > 0).length);
  const reversados = Number(stats?.reversados ?? 0);



  const copyToClipboard = async (text: string, type: "link" | "code") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(type === "link" ? "Link copiado" : "Código copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Error al copiar");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "registered":
      case "converted":
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Registrado</Badge>;
      case "pending":
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Pendiente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "registered":
      case "converted":
        return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case "pending":
        return <Clock className="w-5 h-5 text-amber-400" />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <MousePointerClick className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{clicks}</p>
                <p className="text-xs text-muted-foreground">
                  Clics en tu link{clicks > 0 ? ` · ${clickToSignup}% registro` : ''}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">

          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{totalReferrals}</p>
                <p className="text-xs text-muted-foreground">Referidos Totales</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{registered}</p>
                <p className="text-xs text-muted-foreground">Registrados</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Gift className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{converted}</p>
                <p className="text-xs text-muted-foreground">Con recompensa</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Programa actual */}
      <Card className="glass-card border-amber-500/20 bg-gradient-to-r from-amber-500/5 to-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground mb-1">Gana descuentos por cada referido</h3>
              <p className="text-sm text-muted-foreground">
                {referrerPercent > 0
                  ? `Cuando alguien se registre con tu link y realice su primera compra, recibirás un código de ${referrerPercent}% de descuento para tu próxima compra o renovación.`
                  : 'Comparte tu link y acumula referidos. Pronto activaremos las recompensas.'}
                {referredPercent > 0
                  ? ` Además, tu referido recibe ${referredPercent}% de descuento de bienvenida.`
                  : ''}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumen de mis referidos (estados) */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-base">Resumen de tus referidos</CardTitle>
          <CardDescription>Estado de tus referidos y beneficios</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: 'Referidos registrados', value: registered },
              { label: 'Pendientes de compra', value: pendientesCompra },
              { label: 'Con compra', value: conCompra },
              { label: 'Beneficios generados', value: beneficiosGenerados },
              { label: 'Descuento disponible', value: descuentoDisponible },
              { label: 'Descuentos usados', value: descuentosUsados },
              { label: 'Reversados', value: reversados },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-border/50 bg-background/50 p-3">
                <p className="text-2xl font-bold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Mis recompensas */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            Mis Recompensas
          </CardTitle>
          <CardDescription>Códigos de descuento asignados a tu cuenta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {rewardsLoading && (
            <p className="text-sm text-muted-foreground">Cargando recompensas…</p>
          )}
          {rewardsError && !rewardsLoading && (
            <p className="text-sm text-destructive">No pudimos cargar tus recompensas. Intenta recargar la página.</p>
          )}
          {!rewardsLoading && !rewardsError && rewards.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Aún no tienes recompensas. Recibirás un código cuando alguien se registre con tu link y realice su primera compra.
            </p>
          )}
          {!rewardsLoading && !rewardsError && rewards.map((rw: any) => {
              const used = (rw.times_used ?? 0) >= (rw.max_uses ?? 1);

              const expired = rw.expires_at ? new Date(rw.expires_at) < new Date() : false;
              const available = rw.active && !used && !expired;
              return (
                <div key={rw.id} className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border/50">
                  <div>
                    <p className="font-mono font-bold text-foreground">{rw.code}</p>
                    <p className="text-xs text-muted-foreground">
                      {rw.discount_value}% de descuento
                      {rw.expires_at ? ` · vence ${new Date(rw.expires_at).toLocaleDateString('es-CO')}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {available ? (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Disponible</Badge>
                    ) : (
                      <Badge variant="outline">{used ? 'Utilizado' : expired ? 'Vencido' : 'Reversado'}</Badge>
                    )}
                    <Button variant="outline" size="icon" onClick={() => copyToClipboard(rw.code, 'code')}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
        </CardContent>
      </Card>



      {/* Referral Link */}
      {referralCode && (
        <Card className="glass-card border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-primary" />
              Tu Link de Referido
            </CardTitle>
            <CardDescription>
              Comparte este link con tus amigos y colegas
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Link de referido</label>
              <div className="flex gap-2">
                <Input value={referralLink} readOnly className="bg-background/50 font-mono text-sm" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(referralLink, "link")}>
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Código de referido</label>
              <div className="flex gap-2">
                <Input value={code} readOnly className="bg-background/50 font-mono text-sm font-bold" />
                <Button variant="outline" size="icon" onClick={() => copyToClipboard(code, "code")}>
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* How it works */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            ¿Cómo Funciona?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                <Share2 className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">1. Comparte</h4>
                <p className="text-sm text-muted-foreground">Comparte tu link único</p>
              </div>
            </div>
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">2. Se Registran</h4>
                <p className="text-sm text-muted-foreground">Tu referido se inscribe</p>
              </div>
            </div>
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-amber-500/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h4 className="font-medium text-foreground">3. Gana descuentos</h4>
                <p className="text-sm text-muted-foreground">Recibes tu código al concretarse la compra</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referrals List */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Mis Referidos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No tienes referidos aún. ¡Comparte tu link!</p>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-border/50"
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(referral.status ?? 'pending')}
                    <div>
                      <p className="font-medium text-foreground">{referral.referred_email ?? 'Sin email'}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(referral.created_at).toLocaleDateString('es-CO')}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {getStatusBadge(referral.status ?? 'pending')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralsSection;
